import { Button, generateResolver, Notification, STATUS_TYPES, yup } from "dyl-components";
import React, { useEffect, useState } from "react";
import { Controller, useController, useForm } from "react-hook-form";
import { Form, Grid, Header, Segment } from "semantic-ui-react";
import SignatureCanvasForm from "shared/forms/SignatureCanvasForm";

import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import authService from "actions/auth/auth";
import { Terms } from "./terms";
import { StringUtils } from "utils";
import Html from 'react-pdf-html';
import { Document, Page, View, Text, Image, StyleSheet, usePDF } from "@react-pdf/renderer";
import ReactDOMServer from 'react-dom/server';
import { FILE_CATEGORIES } from 'utils/FileUtils';
import uploadActions from 'actions/upload';
import importActions from 'actions/import_data';
import { FileUtils } from 'utils';

import './index.scss';

const TermsOfService = () => {
    const [isSigning, setIsSigning] = useState(false);
    const [scrollTarget, setScrollTarget] = useState(1);
    const [isRender, setIsRender] = useState(false);
    const [isLoading, setIsLoading] = useState(false);

    const {
        control,
        formState: { isValid, isDirty },
        handleSubmit,
        reset,
        watch,
        trigger,
    } = useForm({
        defaultValues: {
            accepted: false,
            signature: null,
            name: "",
            initial1: "",
            initial2: "",
            initial3: "",
            initial4: "",
            initial5: "",
            initial6: "",
            initial7: "",
            initial8: "",
        },
        resolver: generateResolver({
            accepted: yup.boolean().required("This field is required").oneOf([true], "The terms and conditions must be accepted."),
            name: yup.string().no_whitespace_only().no_excessive_whitespaces().maxlength(64).simple_alphanumeric().required("This field is required"),
            signature: yup.string().required("This field is required"),
            initial1: yup.string().min(2, "This field is required"),
            initial2: yup.string().min(2, "This field is required"),
            initial3: yup.string().min(2, "This field is required"),
            initial4: yup.string().min(2, "This field is required"),
            initial5: yup.string().min(2, "This field is required"),
            initial6: yup.string().min(2, "This field is required"),
            initial7: yup.string().min(2, "This field is required"),
            initial8: yup.string().min(2, "This field is required"),
        }),
        mode: "onChange",
    });

    const { field: signatureField } = useController({
        control,
        name: "signature"
    });

    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { signed_terms, isSigningTerms, customer_id, user_id } = useSelector(state => state.auth);
    const [signature, name, accepted, initial1, initial2, initial3, initial4, initial5, initial6, initial7, initial8] = watch(["signature", "name", "accepted", "initial1", "initial2", "initial3", "initial4", "initial5", "initial6", "initial7", "initial8"]);
    
    
    const date = new Date(Date.now()).toDateString();
    const fontStyles = StyleSheet.create({
        page: {
            fontFamily: 'Helvetica', 
            fontSize: 10,
            padding: 30,
        },
        view: {
            marginTop: 20, 
            textAlign: 'center', 
            display: 'flex', 
            justifyContent: 'center', 
            alignItems: 'center'
        },
        image: {
            width: 200, 
            height: 50, 
            marginTop: 25,
            marginBottom: 25
        }
    });
    const TOSDocument = ({signature, name, date, control, render}) => {
        return(
        <Document>
            <Page size="A4" style={fontStyles.page}>
                <Html style={fontStyles.page}>
                    {ReactDOMServer.renderToStaticMarkup(<Terms signature={signatureField.value} name={name} control={control} scrollNext={scrollNext} scrollTarget={scrollTarget} accepted={accepted} date={date} render={render}/>)}
                </Html>
                 <View style={fontStyles.view}>
                    <Text>I agree to the terms of service:</Text>
                    <Image src={signature} style={fontStyles.image}/>
                    <Text>{name}</Text>
                    <Text>Date: {date}</Text>
                </View>
            </Page>
        </Document>
    )};

    const [instance, update] = usePDF({ document: <TOSDocument signature={signature} name={name} date={date} control={control} render/> });
    
    useEffect(() => {
        if(name !== "" && signature !== null && initial1 !== "" && initial2 !== "" && initial3 !== "" && initial4 !== "" && initial5 !== "" && initial6 !== "" && initial7 !== "" && initial8 !== ""){
            setIsLoading(true);
            setTimeout(() => {
                setIsRender(true);
            }, 100)
        } else {
            setIsRender(false);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [name, signature, initial1, initial2, initial3, initial4, initial5, initial6, initial7, initial8]); 

    useEffect(() => {
        if(name !== "" && signature !== null){
            scrollInitial();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [name, signature]); 

    useEffect(() => {
        if(!instance) return;
        if(!instance?.loading){
            setIsLoading(false);
            if(isRender){
                setTimeout(() => {
                    scrollToTarget(9);
                }, 100)
            }
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [instance?.loading]); 

     useEffect(() => {
        if(isRender){
            update(<TOSDocument signature={signature} name={name} date={date} control={control} render/>);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [ isRender ]); 


    const onAccept = async () => {
        const fileName = `TermsOfService_${customer_id}.pdf`;
        const type = instance?.blob?.type;
        const file = new File([instance?.blob], fileName, { type: type })
        const fileBase64 = await FileUtils.readFileToBase64(file);
        const removeMimeDeclartion = fileBase64.split(",")[1];
        const files = [file];
        const uploadedFiles = await dispatch(uploadActions.uploadFiles(files, FILE_CATEGORIES.DOCUMENT, [], null, false, true));
        const newFiles = files.map((f, idx) => {
            return {
                name: f.name,
                file_id: uploadedFiles[idx],
                size: f.size,
                account_id: customer_id,
                created_user_id: user_id
            }
        })
        try {
            await dispatch(authService.signTerms(null, { file: removeMimeDeclartion, filename: fileName, mimetype: type }, { signed: true }));
            await dispatch(importActions.uploadProfile(newFiles, { terms: true }));
            await dispatch(authService.check());
        } catch (e) {
            Notification.alert("Failed to sign terms", STATUS_TYPES.ERROR);
        }
    }

    const scrollNext = () => {
        StringUtils.scrollToID(scrollTarget)
        setScrollTarget(prevQueue => prevQueue + 1);
    }

    const scrollInitial = () => {
        if(scrollTarget > 1) {
            StringUtils.scrollToID(scrollTarget - 1)
        } else {
            StringUtils.scrollToID(1)
        }
    }

    const scrollToTarget = (target) => {
        StringUtils.scrollToID(target)
    }
    

    const onReset = () => {
        reset();
        trigger();
        setIsRender(false);
        setScrollTarget(1);
    }

    useEffect(() => {
        if (!signed_terms) {
            trigger();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    
    useEffect(() => {
        if (signed_terms) {
            navigate("/");
        }
    }, [navigate, signed_terms]);

    return (
        <div className="TermsOfServiceWrapper">
            <Segment className="TermsOfService">
                <Form noValidate loading={isSigningTerms || isLoading}>
                    <Grid>
                        <Grid.Row columns={"equal"}>
                            <Grid.Column textAlign="center" width={11}>
                                <div className="TermsOfService__scroll-container">
                                    <Terms signature={signatureField.value} name={name} control={control} scrollNext={scrollNext} scrollTarget={scrollTarget} accepted={accepted} date={date} setScrollTarget={setScrollTarget}/>
                                </div>
                            </Grid.Column>
                            <Grid.Column>
                                <Header color="primary">Accept Terms of Service</Header>
                                <p>Verify that you accept the DYL Terms of Service.</p>
                                <Controller
                                    control={control}
                                    name="accepted"
                                    render={({ field: { name, value, onChange }, fieldState: { error } }) => (
                                        <Form.Checkbox
                                            checked={value}
                                            onChange={(_, { checked }) => {
                                                onChange({
                                                    target: { name, value: checked },
                                                });
                                            }}
                                            label="I accept the DYL Terms of Service."
                                             error={error && error.message && {
                                                pointing: 'below'
                                            }}
                                            className="TermsOfService__initial--input"
                                        />
                                    )}
                                />
                                <Controller 
                                    control={control}
                                    name="name"
                                    render={({ field: { name, value, onChange }, fieldState: { error } }) => (
                                        <Form.Input 
                                            label="Name"
                                            required
                                            onChange={(_, { value }) => {onChange({ target: { name, value } })}}
                                            value={value}
                                             error={error && error.message && {
                                                pointing: 'below'
                                            }}
                                            className="TermsOfService__initial--input"
                                        />
                                    )}
                                />
                                <Controller 
                                    control={control}
                                    name="signature"
                                    render={({ fieldState: { error } }) => (
                                        <Form.Field
                                            label="Signature"
                                            required
                                            control="div"
                                            error={error && error.message && {
                                                pointing: 'below'
                                            }}
                                        >
                                            {signatureField.value ? (
                                            <>
                                                <img className={"TermsOfService__signature"} alt='signature' src={signatureField.value} />
                                                <Button onClick={() => {setIsSigning(true)}} style={{width: '100%'}}>
                                                    Change Signature
                                                </Button>
                                            </>
                                            ) : (
                                                <Button onClick={() => {setIsSigning(true)}} style={{width: '100%'}}>Add Signature</Button>
                                            )}
                                        </Form.Field>
                                    )}
                                />
                                <Grid columns="equal">
                                    <Grid.Row>
                                        <Grid.Column><Button disabled={!isDirty} onClick={() => onReset()} basic style={{width: '100%'}}>Clear</Button></Grid.Column>
                                        <Grid.Column><Button disabled={!isValid || !isDirty} onClick={handleSubmit(onAccept)} style={{width: '100%'}}>Accept</Button></Grid.Column> 
                                    </Grid.Row>
                                </Grid>
                            </Grid.Column>
                        </Grid.Row>
                    </Grid>
                    <SignatureCanvasForm 
                        open={isSigning}
                        onClose={() => {setIsSigning(false)}}
                        name={signatureField.name}
                        onChange={(_, { name, value }) => {signatureField.onChange({ target: { name, value } })}}
                    />
                </Form>
            </Segment>
        </div>
    );
};

export default TermsOfService;
